import * as React from 'react';
import { Outlet, useNavigate } from 'react-router';
import { MarketplaceLeadStatus } from '@gcv/shared';
import UploadIcon from '@mui/icons-material/Upload';
import { Box } from '@mui/system';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ReactComponent as ArchiveIcon } from 'assets/images/archive.svg';
import { useComponent } from 'hooks';
import { useBankMarketplaceLeads } from 'hooks/queries';
import { getAppViewStateStore } from 'stores/AppViewStateStore';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { getFiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import { getFiTemplateStore } from 'stores/FiTemplateStore';
import type { FilterListChild, FilterListItem } from 'ui';
import {
  Container,
  ContainerItem,
  FilterDropdown,
  FilterToggleButton,
  InputSearch,
  MeatballMenu,
  TabbedPage
} from 'ui';
import palette from 'ui/theme/palette';
import useQueryString from 'util/queryString.util';
import { actions } from './actions';
import { InviteDispensaryButton } from './components/invite-dispensary';
import { model } from './model';
import { Active } from './tabs/active';
import { AwaitingReview } from './tabs/awaiting-review';
import { Invited } from './tabs/invited';
import { Leads } from './tabs/leads';
import { ReviewInProgress } from './tabs/review-in-progress';
import { getInitializedFilters } from './utils/getInitializedFilters';

interface MultiActionButtonProps {
  label: string | JSX.Element;
  onClick: () => void;
  disabled?: boolean;
  toolTip?: string;
}

const Index: React.FC = useComponent(() => {
  const navigate = useNavigate();

  const { bulkImports } = useFlags();

  const { bankMarketplaceLeads } = useBankMarketplaceLeads();
  const [tabQuery] = useQueryString('tab', '');
  const appViewStateStore = getAppViewStateStore();
  const dispensaryStore = getFiDispensaryStore();
  const relationshipManagerStore = getFiRelationshipManagerStore();
  const fiTemplateStore = getFiTemplateStore();

  const accountsFilterRef: React.MutableRefObject<HTMLInputElement | null> = React.useRef(null);

  const [filterToggle, setFilterToggle] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState('active');
  const [filterList, setFilterList] = React.useState<FilterListItem[]>(
    getInitializedFilters({ dispensaryStore, relationshipManagerStore, fiTemplateStore }) || []
  );
  const [selectedFilters, setSelectedFilters] = React.useState<FilterListChild[]>([]);

  React.useEffect(() => {
    setCurrentTab(tabQuery || 'active');
    actions.resetMuos();
    actions.loadPermissions();
  }, [tabQuery]);

  React.useEffect(() => {
    if (!model.awaitingReviewCount) {
      actions.setAwaitingReviewCount(model.waitingDispensaries.length);
    }
  }, [model.waitingDispensaries]);

  React.useEffect(() => {
    // We use this handle to set the accounts filter programmatically.
    actions.setAccountsFilterRef(accountsFilterRef);
  }, [accountsFilterRef]);

  React.useEffect(() => {
    updateFilter(appViewStateStore.accountsFilters[currentTab] ?? []);
    updateSelectedFiltersListFromStore(filterList);
  }, [currentTab, filterList]);

  const updateSelectedFiltersListFromStore = (currentFilterList: FilterListItem[]) => {
    currentFilterList.forEach((listItemParent: FilterListItem) => {
      listItemParent.children?.forEach((child) => {
        const previouslySelectedFilter = appViewStateStore.accountsFilters[currentTab]?.find(
          (accountsFilter) =>
            accountsFilter.value === child.value && accountsFilter.parentValue === listItemParent.value
        );

        if (previouslySelectedFilter) {
          child.selected = previouslySelectedFilter.selected;
        } else {
          child.selected = false;
        }
      });
    });

    setFilterList(currentFilterList);
  };

  const updateFilter = (selected: FilterListChild[]) => {
    setSelectedFilters(selected);
    actions.setSearchTerms(selected);
    actions.filterDispensaries(selected);
  };

  const onApply = (selected: FilterListChild[], newFilterList: FilterListItem[]) => {
    appViewStateStore.accountsFilters[currentTab] = selected;
    setFilterToggle(false);
    updateFilter(selected);
    updateSelectedFiltersListFromStore(newFilterList);
  };

  const cancelFilter = () => {
    setTimeout(() => setFilterToggle(false), 1);
    updateSelectedFiltersListFromStore(filterList);
  };

  const toggleFilter = () => {
    setFilterToggle(!filterToggle);
  };

  const optionDropDownOptons: MultiActionButtonProps[] = [
    {
      label: (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ArchiveIcon />
          View Archived Accounts
        </Box>
      ),
      toolTip: '',
      onClick: () => navigate('archived')
    }
  ];

  if (bulkImports) {
    optionDropDownOptons.push({
      label: (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <UploadIcon style={{ color: palette.primary.main, width: '18px', height: '18px' }} />
          Bulk Import
        </Box>
      ),
      toolTip: '',
      onClick: () => navigate('bulk-imports')
    });
  }

  const topActions = (
    <Container
      margin="0"
      padding="8px 0 0 0"
      width={{ xs: '100%', md: 'auto' }}
      align="center"
      flexDirection={{ xs: 'column', sm: 'row' }}
      nowrap
    >
      <ContainerItem padding={{ xs: '0', sm: '0 6px 0 0 ' }} width={{ xs: '100%', sm: 'auto' }}>
        <InputSearch
          headerComponent={true}
          inputRef={accountsFilterRef}
          label="Search accounts"
          onChange={(e) => actions.setAccountsSearchTerm(e.target.value)}
          dataCy="accounts-search"
          defaultValue={model.accountsSearchTerm}
        />
      </ContainerItem>
      <ContainerItem
        width={{ xs: '100%', sm: 'auto' }}
        margin="0"
        padding={{ xs: '12px 0 0 0', sm: '0 6px 0 0 ' }}
      >
        <Box sx={{ position: 'relative' }}>
          <FilterToggleButton
            selectedFilters={selectedFilters.filter((f) => f.selected)}
            toggleFilter={toggleFilter}
            filterToggle={filterToggle}
          />
          {filterToggle && filterList.length ? (
            <FilterDropdown
              filterHeader={'Filters'}
              filterList={filterList}
              includeSearch={true}
              onApplyFilter={onApply}
              onCancel={cancelFilter}
              selectedFilters={selectedFilters}
            />
          ) : null}
        </Box>
      </ContainerItem>

      {!model.isBankExaminer && (
        <ContainerItem
          width={{ xs: '100%', sm: 'auto' }}
          margin="0"
          padding={{ xs: '12px 0 0 0', sm: '0 6px 0 0 ' }}
        >
          <InviteDispensaryButton />
        </ContainerItem>
      )}

      <ContainerItem
        width={{ xs: '100%', sm: 'auto' }}
        margin="0"
        padding={{ xs: '12px 0 0 0', sm: '0 6px 0 0 ' }}
      >
        <MeatballMenu menuOptions={optionDropDownOptons} />
      </ContainerItem>
    </Container>
  );

  const tabs = model.isBankExaminer
    ? [{ title: 'Active', component: Active, id: 'active', topActions }]
    : [
        { title: 'Active', component: Active, id: 'active', topActions },
        { title: 'Review In Progress', component: ReviewInProgress, id: 'review-in-progress', topActions },
        {
          title: 'Awaiting Review',
          component: AwaitingReview,
          id: 'awaiting-review',
          topActions,
          notificationCount: model.awaitingReviewCount
        },
        { title: 'Invited', component: Invited, id: 'invited', topActions },
        {
          title: 'Leads',
          component: Leads,
          id: 'leads',
          notificationCount: bankMarketplaceLeads?.filter((l) => l.status === MarketplaceLeadStatus.New)
            .length
        }
      ];

  return (
    <>
      <Outlet />
      <TabbedPage
        dataCy="accounts-tab"
        title="Accounts"
        tabs={tabs}
        onTabChange={(tab) => setCurrentTab(tab.toLowerCase().replaceAll(' ', '-'))}
      />
    </>
  );
});

export default Index;
